/**
 * PosDriftOverlayContent — live in-game chart of position drift
 * (client physics vs. validated claim broadcast).
 *
 * Built as Lever 2's live counterpart to
 * scripts/qa/pos-drift-recorder/. That kit captures CSVs for offline
 * analysis; THIS overlay lets DJ see the drift in real time during
 * gameplay so spikes can be felt-vs-seen instantly.
 *
 * Sample at 20Hz. Keep a rolling 5s window (100 samples). Render via
 * canvas — three traces overlaid on the same x-axis (elapsed time):
 *   - dx (red)        : client.x - server.x
 *   - dy (green)      : client.y - server.y
 *   - distance (purple): hypot(dx, dy) — the headline drift number
 *
 * Server position is from `room.state.players.get(sessionId)` (the
 * Colyseus-synced authoritative schema). Client position is from the
 * GameHost → GameScene → EntitySystem → playerManager.getPlayer()
 * .position chain (same traversal as the offline recorder).
 *
 * 20Hz × 3 numbers + a tiny canvas redraw per tick = effectively zero
 * impact when enabled. Default OFF so non-debug sessions pay nothing.
 *
 * DEBUG-POSDRIFT-WIRE-01 (2026-07-27): the `sΔ / cΔ / s/c / peak` readouts
 * used to render their initial-state zeroes whether or not a single valid
 * sample had ever been taken, so an overlay with NO producer looked exactly
 * like a perfectly-synced player. It is the second confirmed instance of
 * `physicsSafety`'s bug (`cc39e658`) and it is the live state of this tool:
 * `getServerState()` bails on `seq === 0`, and a human player's
 * `lastProcessedSeq` has been permanently 0 since client-owned movement
 * (`ee8896b2`, 2026-05-26) made the only write site bot-only. The readouts
 * now refuse to answer until a sample actually backs them — the call the
 * aggro overlay made when it drew "unknown" rather than a plausible constant.
 * See docs/architecture/measurement-parity.md.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CopyButton } from '../CopyButton';
/* eslint-disable @typescript-eslint/no-explicit-any */

const HZ = 20;
const SAMPLE_INTERVAL_MS = Math.round(1000 / HZ);
const WINDOW_SEC = 5;
const BUFFER_SIZE = HZ * WINDOW_SEC; // 100 samples

interface DriftSample {
  t: number;     // monotonic ms since recorder start
  dx: number | null;
  dy: number | null;
  dist: number | null;
  sx: number | null;   // server position x
  sy: number | null;   // server position y
  cx: number | null;   // client position x
  cy: number | null;   // client position y
  seq: number | null;  // server lastProcessedSeq
}

/**
 * Whether the sampler is actually being fed, and by which side. An empty
 * drift buffer means nothing without this alongside it — and because the two
 * reads fail independently, naming the failing side is what makes the amber
 * banner actionable instead of merely honest.
 */
export interface DriftSamplerStatus {
  /** Sampler ticks since the last reset. */
  samples: number;
  /** Ticks where `getServerState()` returned a position. */
  serverReads: number;
  /** Ticks where `getClientPos()` returned a position. */
  clientReads: number;
  /** Ticks where BOTH sides read — the only ticks that produce a drift number. */
  validSamples: number;
}

const EMPTY_STATUS: DriftSamplerStatus = { samples: 0, serverReads: 0, clientReads: 0, validSamples: 0 };

function getServerState(): { x: number; y: number; seq: number } | null {
  const nm = (globalThis as any).nm;
  const room = nm?.getRoom?.();
  if (!room || !room.sessionId) return null;
  const p = room.state?.players?.get?.(room.sessionId);
  if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
  const seq = typeof p.lastProcessedSeq === 'number' ? p.lastProcessedSeq : 0;
  // Skip fresh spawn / respawn — server hasn't acked any client input yet
  // and the client may be at a different default spawn position.
  if (seq === 0) return null;
  return { x: p.x, y: p.y, seq };
}

function getClientPos(): { x: number; y: number } | null {
  try {
    const host = (globalThis as any).__GAME_HOST__;
    const scene = host?._sceneRegistry?.active;
    const kernel = scene?.lifecycleManager?.kernel || scene?.lifecycleManager?._kernel;
    const systems = kernel?.orderedSystems || [];
    const es = Array.isArray(systems) ? systems.find((s: any) => s?.id === 'EntitySystem') : null;
    const pm = es?.playerManager;
    const player = pm && typeof pm.getPlayer === 'function' ? pm.getPlayer() : null;
    const pos = player?.position;
    if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
      return { x: pos.x, y: pos.y };
    }
  } catch { /* fall through */ }
  return null;
}

function drawChart(
  canvas: HTMLCanvasElement,
  samples: DriftSample[],
  enabled: boolean,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  // Background
  ctx.fillStyle = '#0d0d12';
  ctx.fillRect(0, 0, W, H);

  if (!enabled) {
    ctx.fillStyle = '#666';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('(disabled)', W / 2, H / 2);
    return;
  }
  if (samples.length === 0) {
    ctx.fillStyle = '#666';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('waiting for data…', W / 2, H / 2);
    return;
  }

  // Auto-scale y to data, with a floor so tiny drift isn't visually exaggerated.
  const Y_MIN_RANGE = 20; // px — distance below this stays at this baseline so the chart isn't twitchy
  let absMax = Y_MIN_RANGE;
  for (const s of samples) {
    if (s.dx !== null) absMax = Math.max(absMax, Math.abs(s.dx));
    if (s.dy !== null) absMax = Math.max(absMax, Math.abs(s.dy));
    if (s.dist !== null) absMax = Math.max(absMax, s.dist);
  }
  // Round to nice tick
  const niceMax = Math.ceil(absMax / 10) * 10;

  // Coordinate transforms
  const padL = 36;
  const padR = 6;
  const padT = 6;
  const padB = 14;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  // Time spans the buffer window
  const tMin = samples[0].t;
  const tMax = samples[samples.length - 1].t;
  const tRange = Math.max(1, tMax - tMin);
  const xOf = (t: number) => padL + ((t - tMin) / tRange) * innerW;
  // dy is symmetric around 0; distance is always >= 0 but we plot on same axis
  // Show 0 at the vertical center. distance shown as offset above 0.
  const midY = padT + innerH / 2;
  const yOf = (v: number) => midY - (v / niceMax) * (innerH / 2);

  // Gridlines (zero + ±niceMax/2)
  ctx.strokeStyle = '#222';
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 2]);
  ctx.beginPath();
  ctx.moveTo(padL, midY);
  ctx.lineTo(padL + innerW, midY);
  ctx.moveTo(padL, yOf(niceMax / 2));
  ctx.lineTo(padL + innerW, yOf(niceMax / 2));
  ctx.moveTo(padL, yOf(-niceMax / 2));
  ctx.lineTo(padL + innerW, yOf(-niceMax / 2));
  ctx.stroke();
  ctx.setLineDash([]);

  // Y-axis labels
  ctx.fillStyle = '#888';
  ctx.font = '9px monospace';
  ctx.textAlign = 'right';
  ctx.fillText(`+${niceMax}`, padL - 4, padT + 8);
  ctx.fillText('0', padL - 4, midY + 3);
  ctx.fillText(`-${niceMax}`, padL - 4, padT + innerH);

  // X-axis label
  ctx.textAlign = 'left';
  ctx.fillText(`−${WINDOW_SEC}s`, padL, H - 2);
  ctx.textAlign = 'right';
  ctx.fillText('now', padL + innerW, H - 2);

  // Trace plotter
  const plot = (color: string, get: (s: DriftSample) => number | null) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    let started = false;
    for (const s of samples) {
      const v = get(s);
      if (v === null || !Number.isFinite(v)) {
        started = false;
        continue;
      }
      const px = xOf(s.t);
      const py = yOf(v);
      if (!started) {
        ctx.moveTo(px, py);
        started = true;
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.stroke();
  };
  plot('#e74c3c', (s) => s.dx);       // red
  plot('#2ecc71', (s) => s.dy);       // green
  plot('#9b59b6', (s) => s.dist);     // purple

  // Current values as text overlay (top-right)
  const last = samples[samples.length - 1];
  if (last && last.dx !== null && last.dy !== null && last.dist !== null) {
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#e74c3c';
    ctx.fillText(`dx ${last.dx.toFixed(1)}`, W - padR, padT + 10);
    ctx.fillStyle = '#2ecc71';
    ctx.fillText(`dy ${last.dy.toFixed(1)}`, W - padR, padT + 22);
    ctx.fillStyle = '#9b59b6';
    ctx.fillText(`dist ${last.dist.toFixed(1)}`, W - padR, padT + 34);
    if (last.sx !== null && last.cx !== null) {
      ctx.fillStyle = '#666';
      ctx.fillText(`s(${last.sx.toFixed(0)},${last.sy?.toFixed(0)})`, W - padR, padT + 48);
      ctx.fillText(`c(${last.cx.toFixed(0)},${last.cy?.toFixed(0)})`, W - padR, padT + 60);
    }
  }
}

export const PosDriftOverlayContent: React.FC = () => {
  const [enabled, setEnabled] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bufferRef = useRef<DriftSample[]>([]);
  // Forces re-render of the text summary at a lower rate than the canvas.
  const [summaryTick, setSummaryTick] = useState(0);
  const startRef = useRef<number>(Date.now());
  // Most-recent peak distance over the window — surfaced as a "5s peak" readout.
  const [peakDist, setPeakDist] = useState(0);
  // Server/client movement deltas over the window (diagnostic for key-hold drift root cause).
  const [serverDelta, setServerDelta] = useState(0);
  const [clientDelta, setClientDelta] = useState(0);
  const [speedRatio, setSpeedRatio] = useState(0);
  // Did anything actually feed us? Kept beside the numbers it qualifies.
  const statusRef = useRef<DriftSamplerStatus>({ ...EMPTY_STATUS });
  const [status, setStatus] = useState<DriftSamplerStatus>({ ...EMPTY_STATUS });

  useEffect(() => {
    if (!enabled) return;
    let summaryCounter = 0;
    const id = setInterval(() => {
      const ss = getServerState();
      const cp = getClientPos();
      const st = statusRef.current;
      st.samples += 1;
      if (ss) st.serverReads += 1;
      if (cp) st.clientReads += 1;
      if (ss && cp) st.validSamples += 1;
      const t = Date.now() - startRef.current;
      const dx = ss && cp ? cp.x - ss.x : null;
      const dy = ss && cp ? cp.y - ss.y : null;
      const dist = ss && cp ? Math.hypot(cp.x - ss.x, cp.y - ss.y) : null;
      bufferRef.current.push({
        t, dx, dy, dist,
        sx: ss?.x ?? null,
        sy: ss?.y ?? null,
        cx: cp?.x ?? null,
        cy: cp?.y ?? null,
        seq: ss?.seq ?? null,
      });
      if (bufferRef.current.length > BUFFER_SIZE) {
        bufferRef.current.shift();
      }
      // Redraw every tick.
      const canvas = canvasRef.current;
      if (canvas) drawChart(canvas, bufferRef.current, enabled);
      // Update the text summary every 10 ticks (twice per second) so it's
      // readable without flickering.
      summaryCounter += 1;
      if (summaryCounter >= 10) {
        summaryCounter = 0;
        const validDists = bufferRef.current
          .map((s) => s.dist)
          .filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
        const peak = validDists.length > 0 ? Math.max(...validDists) : 0;
        setPeakDist(peak);
        // Server/client movement deltas over the window — is the server moving at all?
        const validSx = bufferRef.current.filter((s) => s.sx !== null);
        const validCx = bufferRef.current.filter((s) => s.cx !== null);
        if (validSx.length >= 2 && validCx.length >= 2) {
          const sFirst = validSx[0].sx!;
          const sLast = validSx[validSx.length - 1].sx!;
          const cFirst = validCx[0].cx!;
          const cLast = validCx[validCx.length - 1].cx!;
          setServerDelta(sLast - sFirst);
          setClientDelta(cLast - cFirst);
          setSpeedRatio(Math.abs(cLast - cFirst) > 0.5 ? (sLast - sFirst) / (cLast - cFirst) : 0);
        }
        setStatus({ ...statusRef.current });
        setSummaryTick((n) => n + 1);
      }
    }, SAMPLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled]);

  const reset = useCallback(() => {
    bufferRef.current = [];
    setPeakDist(0);
    statusRef.current = { ...EMPTY_STATUS };
    setStatus({ ...EMPTY_STATUS });
    setServerDelta(0);
    setClientDelta(0);
    setSpeedRatio(0);
    startRef.current = Date.now();
    const canvas = canvasRef.current;
    if (canvas) drawChart(canvas, [], enabled);
  }, [enabled]);

  const copyText = () => {
    const all = bufferRef.current;
    if (all.length === 0) return 'no samples';
    const valid = all.filter((s): s is DriftSample & { dx: number; dy: number; dist: number; sx: number; sy: number; cx: number; cy: number } =>
      s.dist !== null && s.sx !== null && s.cx !== null
    );
    const dists = valid.map((s) => s.dist);
    const mean = dists.length > 0 ? dists.reduce((a, b) => a + b, 0) / dists.length : 0;
    const max = dists.length > 0 ? Math.max(...dists) : 0;
    const sorted = [...dists].sort((a, b) => a - b);
    const p95 = sorted.length > 0 ? sorted[Math.floor(sorted.length * 0.95)] : 0;
    const first = valid.length > 0 ? valid[0] : null;
    const last = valid.length > 0 ? valid[valid.length - 1] : null;

    const lines: string[] = [];
    lines.push(`POS DRIFT @ ${new Date().toISOString()}`);
    lines.push(`window: ${WINDOW_SEC}s @ ${HZ}Hz (${all.length}/${BUFFER_SIZE} samples, ${valid.length} valid)`);
    if (last && first) {
      lines.push(`current: dx=${last.dx.toFixed(2)} dy=${last.dy.toFixed(2)} dist=${last.dist.toFixed(2)}  seq=${last.seq ?? '?'}`);
      lines.push(`deltas: server Δx=${(last.sx - first.sx).toFixed(0)}px  client Δx=${(last.cx - first.cx).toFixed(0)}px  ratio=${(Math.abs(last.cx - first.cx) > 0.5 ? (last.sx - first.sx) / (last.cx - first.cx) : 0).toFixed(3)}`);
      lines.push(`summary: mean=${mean.toFixed(2)}px p95=${p95.toFixed(2)}px max=${max.toFixed(2)}px`);
    }
    lines.push('');
    lines.push('─── per-sample matrix ───');
    lines.push(`${'t'.padStart(5)} | ${'sx'.padStart(7)} | ${'sy'.padStart(7)} | ${'cx'.padStart(7)} | ${'cy'.padStart(7)} | ${'dx'.padStart(7)} | ${'dy'.padStart(7)} | ${'dist'.padStart(7)} | seq`);
    lines.push(`${'─'.repeat(5)}─┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼${'─'.repeat(8)}┼────`);
    for (const s of all) {
      const t = (s.t / 1000).toFixed(2);
      const sx = s.sx !== null ? s.sx.toFixed(1).padStart(7) : '   null';
      const sy = s.sy !== null ? s.sy.toFixed(1).padStart(7) : '   null';
      const cx = s.cx !== null ? s.cx.toFixed(1).padStart(7) : '   null';
      const cy = s.cy !== null ? s.cy.toFixed(1).padStart(7) : '   null';
      const dx = s.dx !== null ? s.dx.toFixed(1).padStart(7) : '   null';
      const dy = s.dy !== null ? s.dy.toFixed(1).padStart(7) : '   null';
      const dist = s.dist !== null ? s.dist.toFixed(1).padStart(7) : '   null';
      const seq = s.seq !== null ? String(s.seq) : '?';
      lines.push(`${t.padStart(5)} | ${sx} | ${sy} | ${cx} | ${cy} | ${dx} | ${dy} | ${dist} | ${seq}`);
    }
    return lines.join('\n');
  };

  // Read the last sample for the readout. `summaryTick` is in the deps
  // of the surrounding hook chain (via useState in the interval handler)
  // so the component re-renders when summaryTick changes and we read the
  // freshest sample from the buffer ref below.
  void summaryTick;
  const last = bufferRef.current[bufferRef.current.length - 1] ?? null;

  // A drift number exists only where BOTH sides read. Until one does, every
  // derived readout below is its initial zero, not a measurement — so it is
  // rendered as "—" and the banner says which side never arrived.
  const hasData = status.validSamples > 0;
  const starved = status.samples > 0 && status.validSamples === 0;
  const missingSide = status.serverReads === 0 && status.clientReads === 0
    ? 'neither the server schema nor the client entity'
    : status.serverReads === 0
      ? 'the server schema (room.state.players — bails while lastProcessedSeq is 0)'
      : 'the client entity (GameHost → GameScene → EntitySystem → playerManager)';
  const num = (fn: () => string): string => (hasData ? fn() : '—');

  return (
    <div style={{ padding: 6, fontSize: 11, color: '#ccc' }} data-testid="pos-drift-overlay">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="pos-drift-enabled"
          />
          enabled
        </label>
        <button
          onClick={reset}
          data-testid="pos-drift-reset"
          style={{
            fontSize: 10,
            padding: '2px 8px',
            background: 'rgba(231,76,60,0.15)',
            color: '#e74c3c',
            border: '1px solid rgba(231,76,60,0.3)',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          reset
        </button>
        <CopyButton getText={copyText} label="📋 Copy Detailed" />
        <span style={{ marginLeft: 'auto', color: '#888', fontSize: 10 }}>
          {WINDOW_SEC}s @ {HZ}Hz
        </span>
      </div>

      <canvas
        ref={canvasRef}
        width={380}
        height={140}
        style={{ width: '100%', maxWidth: 480, border: '1px solid #333', borderRadius: 3, display: 'block' }}
      />

      {starved && (
        // No producer ⇒ no verdict. Amber, never a number: an unfed sampler
        // is not a synced player. Mirrors PhysicsSafetyOverlay (cc39e658).
        <div
          data-testid="pos-drift-not-wired"
          style={{
            marginTop: 6,
            padding: 8,
            border: '1px solid rgba(241,196,15,0.35)',
            borderRadius: 3,
            color: '#f1c40f',
            fontSize: 10,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 'bold' }}>⚠️ NOT WIRED — no data</div>
          <div style={{ color: '#aaa', marginTop: 3 }}>
            {status.samples} sample(s) taken, <strong>0</strong> usable — {missingSide} never
            reported. The readouts below are <strong>not</strong> a zero-drift result.
          </div>
          <div style={{ color: '#888', marginTop: 3 }}>
            server reads {status.serverReads}/{status.samples} · client reads{' '}
            {status.clientReads}/{status.samples}
          </div>
        </div>
      )}

      {last && last.sx !== null && last.cx !== null && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#999', fontFamily: 'monospace', lineHeight: '1.6' }}>
          <div>server ({last.sx.toFixed(1)}, {last.sy?.toFixed(1)})  client ({last.cx.toFixed(1)}, {last.cy?.toFixed(1)})</div>
          <div style={{ color: '#666' }}>seq {last.seq ?? '?'}  dx={last.dx?.toFixed(1)} dy={last.dy?.toFixed(1)} dist={last.dist?.toFixed(1)}</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10, color: '#888' }}>
        <span><span style={{ color: '#e74c3c' }}>■</span> dx</span>
        <span><span style={{ color: '#2ecc71' }}>■</span> dy</span>
        <span><span style={{ color: '#9b59b6' }}>■</span> dist</span>
        <span style={{ marginLeft: 'auto', color: '#f39c12' }} data-testid="pos-drift-server-delta">
          sΔ {num(() => `${serverDelta.toFixed(0)}px`)}
        </span>
        <span style={{ color: '#3498db' }} data-testid="pos-drift-client-delta">
          cΔ {num(() => `${clientDelta.toFixed(0)}px`)}
        </span>
        {/* Unfed used to paint this RED (ratio 0.00 < 0.5) — an alarm invented
            out of no data. Grey while unfed; the tiers only apply to a reading. */}
        <span style={{ color: !hasData ? '#888' : speedRatio > 0.9 ? '#2ecc71' : speedRatio > 0.5 ? '#f39c12' : '#e74c3c' }} data-testid="pos-drift-speed-ratio">
          s/c {num(() => speedRatio.toFixed(2))}
        </span>
        <span style={{ color: '#9b59b6' }} data-testid="pos-drift-peak">
          peak {num(() => `${peakDist.toFixed(1)}px`)}
        </span>
        {last && last.dist !== null && (
          <span data-testid="pos-drift-current">
            now {last.dist.toFixed(1)}px
          </span>
        )}
      </div>
    </div>
  );
};
